import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAutosaveIndicator } from "@/hooks/useAutosaveIndicator";
import { SaveChip } from "@/components/ui/SaveChip";
import { InfrastructureScopeCard } from "./InfrastructureScopeCard";
import { TelecomControlsSection } from "./TelecomControlsSection";
// telecom_v2 reviewer UI is scoped exclusively to telecom-specific C1–C8
// modules. Legacy generic (B1–B4) controls are not rendered here; their
// catalog rows, RPCs and historical data are preserved for non-telecom
// assessments and backward compatibility. Server-side enforcement via
// public.is_legacy_b_infrastructure_control() excludes B1–B4 from
// telecom_v2 Infrastructure Security scoring.
import { LEGACY_FINDING_TEMPLATES_BY_KEY as INFRASTRUCTURE_FINDING_TEMPLATES } from "@/configs/controlGuidance/infrastructureFindingTemplates";

const sb: any = supabase as any;

type Status = "Implemented" | "Partially Implemented" | "Not Implemented" | "Not Applicable";

interface ControlConfig {
  key: string;
  label: string;
  category: string;
}

// Reviewer-driven Infrastructure Security checklist (telecom_v2).
// Data model supports optional scoring via scoring_enabled / score_value columns.
const INFRASTRUCTURE_CONTROLS: ControlConfig[] = [
  // Physical Access Security
  { key: "facility_access_controls", label: "Facility access controls", category: "Physical Access Security" },
  { key: "visitor_management",       label: "Visitor management",        category: "Physical Access Security" },
  { key: "badge_biometric_access",   label: "Badge / biometric access",  category: "Physical Access Security" },
  { key: "cctv_coverage",            label: "CCTV coverage",             category: "Physical Access Security" },
  { key: "rack_cage_security",       label: "Rack / cage security",      category: "Physical Access Security" },

  // Environmental Controls
  { key: "fire_suppression",         label: "Fire suppression",                  category: "Environmental Controls" },
  { key: "hvac_environmental",       label: "HVAC / environmental monitoring",   category: "Environmental Controls" },

  // Power & Cooling Resilience
  { key: "ups_generator",            label: "UPS / generator resilience",        category: "Power & Cooling Resilience" },

  // Network Infrastructure Protection
  { key: "network_room_protection",  label: "Network room protection",           category: "Network Infrastructure Protection" },
  { key: "cable_pathway_security",   label: "Cable / pathway security",          category: "Network Infrastructure Protection" },
  { key: "ddos_edge_protection",     label: "DDoS / network edge protection",    category: "Network Infrastructure Protection" },

  // Monitoring & Facilities Operations
  { key: "site_incident_response",   label: "Site incident response procedures", category: "Monitoring & Facilities Operations" },
];


interface Row {
  control_key: string;
  control_label: string;
  category: string;
  status: string | null;
  reviewer_notes: string | null;
  evidence_url: string | null;
  scoring_enabled: boolean;
  add_to_findings: boolean;
}

// Statuses that allow CAP integration via auto-generated findings.
const FINDING_ELIGIBLE: Status[] = ["Partially Implemented", "Not Implemented"];
function isFindingEligibleStatus(status: string | null): boolean {
  return !!status && (FINDING_ELIGIBLE as string[]).includes(status);
}


interface Props {
  assessmentId: string;
  disabled?: boolean;
  /**
   * Fired after any successful save/upsert/delete to assessment_infrastructure_security.
   * Parent should re-run calculate_csv_domain_scores and bump csvDomainRefreshKey
   * so the Executive Summary (Live) panel reflects the blended Infrastructure Security score.
   */
  onChanged?: () => void;
}

export const InfrastructureSecurityTab: React.FC<Props> = ({ assessmentId, disabled, onChanged }) => {
  const [rows, setRows] = useState<Map<string, Row>>(new Map());
  const [loading, setLoading] = useState(true);
  const autosave = useAutosaveIndicator();
  const scopeAutosave = useAutosaveIndicator();

  // ── Phase 1 Scope state (informational only — does NOT affect scoring) ──
  const [scopeServiceModel, setScopeServiceModel] = useState<string[]>([]);
  const [scopeComponentsPresent, setScopeComponentsPresent] = useState<string[]>([]);
  const [scopeComponentsAssessed, setScopeComponentsAssessed] = useState<string[]>([]);
  const [scopeNotes, setScopeNotes] = useState<string>("");

  useEffect(() => {
    if (!assessmentId) return;
    (async () => {
      setLoading(true);
      const [isResp, fxResp, scopeResp] = await Promise.all([
        sb.rpc("reviewer_get_infrastructure_security", { p_assessment_id: assessmentId }),
        sb.rpc("reviewer_get_findings", { p_assessment_id: assessmentId }),
        sb.rpc("reviewer_get_infrastructure_scope", { p_assessment_id: assessmentId }),
      ]);
      const linkedKeys = new Set<string>(
        (fxResp.data ?? [])
          .map((f: any) => f.infrastructure_control_key)
          .filter(Boolean)
      );
      if (!isResp.error && isResp.data) {
        const map = new Map<string, Row>();
        for (const r of isResp.data) {
          map.set(r.control_key, {
            control_key: r.control_key,
            control_label: r.control_label,
            category: r.category,
            status: r.status,
            reviewer_notes: r.reviewer_notes,
            evidence_url: r.evidence_url,
            scoring_enabled: !!r.scoring_enabled,
            add_to_findings: linkedKeys.has(r.control_key),
          });
        }
        setRows(map);
      }
      const scope = Array.isArray(scopeResp?.data) ? scopeResp.data[0] : scopeResp?.data;
      // Phase 1 (revised) — render the server-side assessment snapshot only.
      // No client-side prefill from the LIVE portfolio: a later portfolio edit
      // must never leak into an existing assessment.
      if (scope) {
        setScopeServiceModel(scope.telecom_service_model ?? []);
        setScopeComponentsPresent(scope.components_present ?? []);
        setScopeComponentsAssessed(scope.components_assessed ?? []);
        setScopeNotes(scope.scope_notes ?? "");
      } else {
        setScopeServiceModel([]);
      }
      setLoading(false);
    })();
  }, [assessmentId]);


  // Save scope fields. Scoring-neutral by contract.
  const saveScope = useCallback(
    async (patch: {
      telecom_service_model?: string[];
      components_present?: string[];
      components_assessed?: string[];
      scope_notes?: string | null;
    }) => {
      scopeAutosave.begin();
      const { error } = await sb.rpc("reviewer_upsert_infrastructure_scope", {
        p_assessment_id: assessmentId,
        p_telecom_service_model: patch.telecom_service_model ?? scopeServiceModel,
        p_components_present: patch.components_present ?? scopeComponentsPresent,
        p_components_assessed: patch.components_assessed ?? scopeComponentsAssessed,
        p_scope_notes: patch.scope_notes ?? scopeNotes,
      });
      if (error) {
        console.error("upsert infra scope error", error);
        scopeAutosave.fail();
      } else {
        scopeAutosave.succeed();
        // NOTE: scope is informational only in Phase 1 — do NOT call onChanged()
        // so the parent does not trigger a score recompute.
      }
    },
    [assessmentId, scopeAutosave, scopeServiceModel, scopeComponentsPresent, scopeComponentsAssessed, scopeNotes],
  );

  // Sync helper: create or remove the linked finding for a given control.
  const syncFinding = useCallback(
    async (cfg: ControlConfig, enabled: boolean) => {
      const tpl = INFRASTRUCTURE_FINDING_TEMPLATES[cfg.key];
      autosave.begin();
      const { error } = await sb.rpc("reviewer_sync_infrastructure_finding", {
        p_assessment_id: assessmentId,
        p_control_key: cfg.key,
        p_enabled: enabled,
        p_finding_title: tpl?.finding_title ?? cfg.label,
        p_priority_level: tpl?.priority_level ?? "Strategic",
        p_recommended_action: tpl?.recommended_action ?? null,
        p_timeframe: tpl?.timeframe ?? "3–6 months",
        p_recommended_measures: tpl?.recommended_measures ?? null,
        p_remediation_path: tpl?.remediation_path ?? null,
        p_risk_level: tpl?.risk_level ?? "Moderate",
        // p_control_code intentionally omitted: server resolves canonical NIST
        // code(s) from infrastructure_control_mapping based on p_control_key.
        p_control_code: null,
      });
      if (error) {
        console.error("sync infra finding error", error);
        autosave.fail();
      } else {
        autosave.succeed();
      }
    },
    [assessmentId, autosave]
  );


  const upsert = useCallback(
    async (r: Row) => {
      autosave.begin();
      // SAFETY: telecom-specific control rows must not flow into the legacy
      // generic Infrastructure Security scoring path. Force scoring_enabled
      // to false for any control whose key is not in the legacy generic set.
      const isGeneric = INFRASTRUCTURE_CONTROLS.some((c) => c.key === r.control_key);
      const scoringEnabled = isGeneric ? r.scoring_enabled : false;
      const { error } = await sb.rpc("reviewer_upsert_infrastructure_security", {
        p_assessment_id: assessmentId,
        p_control_key: r.control_key,
        p_control_label: r.control_label,
        p_category: r.category,
        p_status: r.status,
        p_reviewer_notes: r.reviewer_notes,
        p_evidence_url: r.evidence_url,
        p_scoring_enabled: scoringEnabled,
      });
      if (error) {
        console.error("upsert infra security error", error);
        autosave.fail();
      } else {
        autosave.succeed();
        onChanged?.();
      }
    },
    [assessmentId, autosave, onChanged]
  );

  const remove = useCallback(
    async (controlKey: string) => {
      autosave.begin();
      const { error } = await sb.rpc("reviewer_delete_infrastructure_security", {
        p_assessment_id: assessmentId,
        p_control_key: controlKey,
      });
      if (error) {
        console.error("delete infra security error", error);
        autosave.fail();
      } else {
        autosave.succeed();
        onChanged?.();
      }
    },
    [assessmentId, autosave, onChanged]
  );

  const handleToggle = useCallback(
    (cfg: ControlConfig, checked: boolean) => {
      setRows((prev) => {
        const next = new Map(prev);
        if (checked) {
          const r: Row = {
            control_key: cfg.key,
            control_label: cfg.label,
            category: cfg.category,
            status: null,
            reviewer_notes: null,
            evidence_url: null,
            scoring_enabled: false,
            add_to_findings: false,
          };
          next.set(cfg.key, r);
          upsert(r);
        } else {
          const existing = next.get(cfg.key);
          next.delete(cfg.key);
          remove(cfg.key);
          // If a finding was linked, remove it too.
          if (existing?.add_to_findings) {
            syncFinding(cfg, false);
          }
        }
        return next;
      });
    },
    [upsert, remove, syncFinding]
  );

  const updateField = useCallback(
    (controlKey: string, patch: Partial<Row>) => {
      setRows((prev) => {
        const next = new Map(prev);
        const existing = next.get(controlKey);
        if (!existing) return prev;

        const updated: Row = { ...existing, ...patch };

        // When status changes, apply CAP defaults:
        //   Not Implemented        → auto-add to findings (default on)
        //   Partially Implemented  → leave for reviewer (default off)
        //   Implemented / N/A      → remove any linked finding
        if ("status" in patch && patch.status !== existing.status) {
          const cfg =
            INFRASTRUCTURE_CONTROLS.find((c) => c.key === controlKey) ??
            { key: controlKey, label: existing.control_label, category: existing.category };
          const newStatus = updated.status as Status | null;
          if (newStatus === "Not Implemented" && !existing.add_to_findings) {
            updated.add_to_findings = true;
            syncFinding(cfg, true);
          } else if (
            newStatus &&
            !isFindingEligibleStatus(newStatus) &&
            existing.add_to_findings
          ) {
            updated.add_to_findings = false;
            syncFinding(cfg, false);
          }
        }

        next.set(controlKey, updated);
        upsert(updated);
        return next;
      });
    },
    [upsert, syncFinding]
  );

  // Explicit toggle for the "Add to Findings" checkbox.
  const toggleAddToFindings = useCallback(
    (cfg: ControlConfig, checked: boolean) => {
      setRows((prev) => {
        const next = new Map(prev);
        const existing = next.get(cfg.key);
        if (!existing) return prev;
        next.set(cfg.key, { ...existing, add_to_findings: checked });
        syncFinding(cfg, checked);
        return next;
      });
    },
    [syncFinding]
  );


  if (loading) {
    return (
      <div className="bg-card rounded-lg border p-8 text-center text-muted-foreground">
        Loading infrastructure security…
      </div>
    );
  }

  const enabledCount = rows.size;


  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Infrastructure Security</h2>
          <p className="text-sm text-muted-foreground">
            Telecom-specific infrastructure controls. Select your service model and assessed
            components below to surface applicable C1–C8 controls.
            {enabledCount > 0 && (
              <span className="ml-2 text-foreground">{enabledCount} control{enabledCount === 1 ? "" : "s"} tracked.</span>
            )}
          </p>
        </div>
        <SaveChip state={autosave.state} />
      </div>

      {/* 1 ── Assessment Scope (drives which telecom modules appear) ── */}
      <InfrastructureScopeCard
        disabled={disabled}
        saveState={scopeAutosave.state}
        serviceModel={scopeServiceModel}
        componentsPresent={scopeComponentsPresent}
        componentsAssessed={scopeComponentsAssessed}
        notes={scopeNotes}
        onServiceModelChange={(v) => { setScopeServiceModel(v); saveScope({ telecom_service_model: v }); }}
        onComponentsPresentChange={(v) => { setScopeComponentsPresent(v); saveScope({ components_present: v }); }}
        onComponentsAssessedChange={(v) => { setScopeComponentsAssessed(v); saveScope({ components_assessed: v }); }}
        onNotesChange={(v) => { setScopeNotes(v); saveScope({ scope_notes: v }); }}
      />

      {/* 2 ── Telecom Infrastructure Controls (C1–C8) ── */}
      {scopeComponentsAssessed.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/20 p-6 text-sm text-muted-foreground text-center">
          Select a Telecom Service Model and mark one or more Infrastructure Components as
          <span className="font-medium text-foreground"> Present + Assessed </span>
          to begin assessing telecom-specific infrastructure controls.
        </div>
      ) : (
        <TelecomControlsSection
          assessmentId={assessmentId}
          rows={rows}
          componentsAssessed={scopeComponentsAssessed}
          disabled={disabled}
          onToggleControl={handleToggle}
          onUpdateField={updateField}
        />
      )}

      {/* Phase 2.7C — Telecom Applicability Blueprint and Telecom Shadow Score
          panels removed from reviewer UI. Components retained on disk for
          internal calibration use; not part of the production workflow. */}


      {/* Phase 2.3 UX correction: standalone "Telecom Findings & Recommendations"
          panel removed — findings now render inline under each telecom control via
          <TelecomControlFindings>. Backing RPCs unchanged; findings still appear
          in the overall Findings tab via reviewer_get_findings. */}

      {/* Legacy generic (B1–B4) controls are intentionally not rendered in the
          telecom_v2 reviewer workflow. B1–B4 catalog rows, RPCs and historical
          responses remain in the database for backward compatibility and
          non-telecom assessments. Server-side
          public.is_legacy_b_infrastructure_control() guarantees they are
          excluded from telecom_v2 scoring. */}
    </div>
  );
};



